<template>
  <div id='pre-qualify-form-page' :class="{ mobile: isMobile() }">
    <CloseButton />
    <div class="header">
      <Asset name="logo.svg"
        alt="Logo Parcelex"
        title="Logo Parcelex"
        class="logo"
      />
      <h2>Verifique agora mesmo sua<br/>avaliação de crédito</h2>
    </div>
    <div class="body" :class="{ mobile: isMobile() }">
      <p>
        É muito importante que você forneça seus dados reais, como CPF e endereço próprios.
      </p>
      <form>
        <div class="form-group">
          <Input
            label="Email"
            placeholder="Seu email"
            :value="user.email"
            @input="formHandler('email', ...arguments)"
            :errorMessage="getMessage('email')"
          />
        </div>
        <div class="form-group">
          <Input
            label="Telefone"
            placeholder="Seu telefone de contato"
            :value="user.phone"
            @input="formHandler('phone', ...arguments)"
            :errorMessage="getMessage('phone')"
            v-mask="'(##) #####-####'"
          />
        </div>
        <div class="form-group">
          <Input
            label="CPF"
            placeholder="11 dígitos, sem traços e pontos"
            :value="user.cpf"
            @input="formHandler('cpf', ...arguments)"
            :errorMessage="getMessage('cpf')"
            v-mask="'###.###.###-##'"
          />
        </div>
        <div class="form-group">
          <Input
            class="input"
            label="CEP de cobrança"
            placeholder="Seu CEP para cobrança"
            :value="user.zipCode"
            @input="formHandler('zipCode', ...arguments)"
            :errorMessage="getMessage('zipCode')"
            v-mask="'#####-###'"
          />
        </div>
      </form>
      <VerifyButton
        @verify="creditVerify"
        @completed="onBtnVerifyFilled"
        :disabled="cantPreApproveMaxTries"
        :enabled="isValidForm"
        ref="verify-btn"
        class="verify-button"
        :firstText="'Verificar meu crédito'"
        :width="isMobile () ? '100%' : '200px'"
      />
      <div v-if="inputErrors && inputErrors.length > 0" class="alert-wrapper">
        {{translateErrorText(inputErrors)}}
      </div>

      <footer>
        Estou de acordo com o
        <a
          href="https://www.parcelex.com.br/uso"
          target="_blank"
        >
          Termos de uso
        </a> e a
        <a
          href="https://www.parcelex.com.br/privacidade"
          target="_blank"
        >
          Política de Privacidade
        </a> do Parcelex.
      </footer>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { mask } from 'vue-the-mask';

import Asset from '@/components/common/Asset.vue';
import Input from '@/components/ui/AppInput.vue';

import UtilMixin from '@/mixins/util';
import DateLib from '@/lib/date';

import VerifyButton from '@/components/common/VerifyButton.vue';
import CloseButton from '../components/CloseButton.vue';
import UpdateIframeHeight from '../mixins/UpdateIframeHeight.vue';

export default {
  name: 'preQualify-form',
  mixins: [UtilMixin, UpdateIframeHeight],
  data: () => ({
    errors: {
      email: {
        message: 'invalid email',
      },
    },
    translateErrorText: (inputErrors) => {
      const textOptions = {
        initialError: 'Não foi possível verificar seu crédito neste momento, tente novamente.',
        maxTries: 'Estamos tendo dificuldade em verificar seu crédito neste momento, tente após algumas horas.',
      };
      if (Array.isArray(inputErrors)) {
        if (inputErrors.length === 1) {
          return textOptions[inputErrors[0]] || '';
        } if (inputErrors.length > 1) {
          return textOptions.initialError;
        }
      }
      return '';
    },
    formInputs: {
      email: {
        currentValue: undefined,
        isValid: false,
        validateFunction: 'isValidEmail',
        errorMessage: 'Email inválido',
        currentMessage: '',
      },
      phone: {
        currentValue: undefined,
        isValid: false,
        validateFunction: 'isValidPhone',
        errorMessage: 'Telefone inválido',
        currentMessage: '',
      },
      cpf: {
        currentValue: undefined,
        isValid: false,
        validateFunction: 'isValidCpf',
        errorMessage: 'CPF inválido',
        currentMessage: '',
      },
      zipCode: {
        currentValue: undefined,
        validateFunction: 'isValidZipCode',
        isValid: false,
        errorMessage: 'CEP inválido',
        currentMessage: '',
      },
    },
  }),
  directives: {
    mask,
  },
  computed: {
    ...mapState('preQualify', [
      'user',
      'downPaymentIndexSelected',
      'downPaymentOptions',
      'installmentOptions',
      'installmentsSkeleton',
      'preApprove',
    ]),
    ...mapGetters('preQualify', ['isApproved']),
    isValidForm() {
      let isValid = true;
      const keys = Object.keys(this.formInputs);
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (!this.formInputs[key].isValid) {
          isValid = false;
          break;
        }
      }
      return isValid;
    },
    cantPreApprove() {
      return this.preApprove
        ? this.preApprove.result === 'NO_PREAPPROVE' : false;
    },
    cantPreApproveMaxTries() {
      return this.preApprove
        ? this.preApprove.result === 'NO_PREAPPROVE_MAX_TRIES' && this.lessThanOneHour() : false;
    },
    inputErrors() {
      if (this.cantPreApproveMaxTries) return ['maxTries'];
      if (this.cantPreApprove) return ['initialError'];
      return [];
    },
  },
  methods: {
    ...mapActions('preQualify', ['getPreApprove', 'goToPage', 'emitEvent']),
    lessThanOneHour() {
      return this.preApprove && this.preApprove.createdAtVuex
        ? DateLib.diffInHours(this.preApprove.createdAtVuex, new Date()) < 1 : true;
    },
    async creditVerify() {
      if (!this.isValidForm) {
        this.errorMessageHandler();
        return;
      }
      try {
        await this.getPreApprove();
        if (this.cantPreApprove || this.cantPreApproveMaxTries) {
          this.$refs['verify-btn'].cantPreApprove();
        } else {
          this.$refs['verify-btn'].complete(this.isApproved);
        }
      } catch (error) {
        let found = false;
        Object.keys(this.errors).forEach((key) => {
          if (this.errors[key].message === error.message) {
            found = true;
            this.formInputs[key].isValid = false;
            this.formInputs[key].currentMessage = this.formInputs[key].errorMessage;
          }
        });

        if (found) {
          this.$refs['verify-btn'].reset();
          this.errorMessageHandler();
        } else {
          this.$emit('onError', error);
        }
      }
    },
    onBtnVerifyFilled() {
      if (!this.cantPreApprove && !this.cantPreApproveMaxTries) {
        if (this.isApproved) {
          this.goToPage('prequalify-approved');
        } else {
          this.goToPage('prequalify-reproved');
        }
      }
    },
    formHandler(type, value) {
      if (!value) return;
      this.$store.commit('preQualify/setUserData', { type, value });
      const { validateFunction } = this.formInputs[type];
      this.formInputs[type].isValid = this[validateFunction](value);
      this.formInputs[type].currentValue = value;
      if (this.formInputs[type].currentMessage !== '') {
        this.formInputs[type].currentMessage = '';
      }
    },
    errorMessageHandler() {
      Object.keys(this.formInputs).forEach((key) => {
        if (!this.formInputs[key].isValid) {
          this.formInputs[key].currentMessage = this.formInputs[key].errorMessage;
        }
      });
    },
    getMessage(type) {
      return this.formInputs[type].currentMessage;
    },
    isValidZipCode(zipCode) {
      return zipCode && zipCode.replace('-', '').length === 8;
    },
  },
  mounted() {
    this.emitEvent({
      type: 'application',
    });
  },
  components: {
    Asset,
    VerifyButton,
    CloseButton,
    Input,
  },
};
</script>

<style lang="scss" scoped>
#pre-qualify-form-page {
  position: relative;
  min-height: 580px;
  margin: 0 auto;

  .header {
    height: 192px;
    padding: 0 20px;

    background-color: var(--primary-extra-light);

    display: flex;
    justify-content: flex-start;
    flex-direction: column;
    align-items: center;
    text-align: center;

    img {
      margin-top: 24px;
    }

    h2 {
      @extend .gray-700;
      margin-top: 36px;
    }
  }

  .body {
    max-width: 360px;
    margin-left: auto;
    margin-right: auto;

    &.mobile {
      max-width: none;
    }

    p {
      @extend .gray-500;
      font-size: 16px;
      line-height: 20px;
      margin-top: 24px;
    }

    form {
      margin-bottom: 20px;
      @media (max-width: 900px) {
        margin-bottom: 40px;
      }

      .form-group {
        margin-top: 20px;

        input {
          margin-top: 4px;
        }

        label {
          @extend .body-bold;
          @extend .gray-600;
        }
      }
    }

    .verify-button {
      margin-top: 20px;
    }

    footer {
      @extend .small-regular;
      @extend .gray-400;
      margin-top: 16px;

      a {
        color: var(--primary);
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  &.mobile {
    margin: 0;
    width: 100%;
    position: static;

    .header {
      height: 100%;
      padding: 0;

      h2 {
        margin: 16px 0px 20px 0px;
        font-size: 20px;
        line-height: 24px;
      }
    }

    .body {
      margin: 0 24px;

      footer {
        margin-bottom: 22px;
      }
    }
  }
}

.alert-wrapper {
  color: var(--danger);
  padding-top: 8px;
}
</style>
